import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Dropdown, Modal } from "react-bootstrap";
import {
  getQuestions,
  getQuestionById,
  createQuestion,
  updateQuestion,
  deleteQuestion,
  uploadQuestionImage,
  deleteQuestionImage,
} from "../../../services/QuestionService";
import { getSubjects } from "../../../services/SubjectService";
import { getQuestionCategories } from "../../../services/QuestionCategoryService";
import { getQuestionDifficulties } from "../../../services/QuestionDifficultyService";
import { getQuestionTypes } from "../../../services/QuestionTypeService";
import { getClinicalCases } from "../../../services/ClinicalCaseService";
import { getReferenceValues } from "../../../services/ReferenceValueService";
import DataTablePagination from "../common/DataTablePagination";
import { usePermissions, PERMISSIONS } from "../../../utils/permissions";
import { useTranslation } from "react-i18next";
import { lazyLabels } from "../../../utils/i18nLabels";
import { IMAGE_ACCEPT, resolveUploadUrl, validateImageFile } from "../../../utils/uploads";

const emptyAnswer = () => ({ content: "", is_correct: false, order_index: 0 });

// Statut de validation (commission des examens) - distinct du booléen
// is_active qui contrôle uniquement la disponibilité de la question.
const statusLabels = lazyLabels({ draft: "common.examStatus.draft", validated: "common.ui.validee", archived: "common.ui.archivee" });
const statusBadge = { draft: "secondary", validated: "success", archived: "warning" };

const emptyForm = {
  subject_id: "",
  category_id: "",
  difficulty_id: "",
  type_id: "",
  content: "",
  explanation: "",
  pedagogical_reference: "",
  points: 1,
  negative_points: 0,
  estimated_duration_seconds: "",
  clinical_case_id: "",
  case_order_index: "",
  requires_calculator: false,
  show_reference_values: false,
  reference_value_ids: [],
  is_active: true,
  answers: [emptyAnswer(), emptyAnswer()],
};

const Questions = () => {
  const { t } = useTranslation();
  // Actions conditionnées par permission (mêmes codes que le backend) :
  // création/modification/suppression par les enseignants, validation par la
  // commission des examens.
  const can = usePermissions();
  const canCreate = can(PERMISSIONS.QUESTION_CREATE);
  const canUpdate = can(PERMISSIONS.QUESTION_UPDATE);
  const canDelete = can(PERMISSIONS.QUESTION_DELETE);

  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");

  const [subjects, setSubjects] = useState([]);
  const [categories, setCategories] = useState([]);
  const [difficulties, setDifficulties] = useState([]);
  const [types, setTypes] = useState([]);
  const [clinicalCases, setClinicalCases] = useState([]);
  const [referenceValues, setReferenceValues] = useState([]);

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [subjectFilter, setSubjectFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const [showFormModal, setShowFormModal] = useState(false);
  const [formMode, setFormMode] = useState("add");
  const [formData, setFormData] = useState(emptyForm);
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [editingId, setEditingId] = useState(null);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [feedback, setFeedback] = useState(null);
  // Image d'illustration de la question : fichier choisi (envoyé après l'enregistrement),
  // aperçu, image déjà enregistrée et demande de retrait.
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const [existingImage, setExistingImage] = useState("");
  const [removeImage, setRemoveImage] = useState(false);
  const [imageError, setImageError] = useState("");

  useEffect(() => {
    getSubjects({ page: 1, limit: 200 }).then((res) => setSubjects(res.data?.data || [])).catch(() => {});
    getQuestionCategories({ page: 1, limit: 200 }).then((res) => setCategories(res.data?.data || [])).catch(() => {});
    getQuestionDifficulties({ page: 1, limit: 200 }).then((res) => setDifficulties(res.data?.data || [])).catch(() => {});
    getQuestionTypes({ page: 1, limit: 200 }).then((res) => setTypes(res.data?.data || [])).catch(() => {});
    getClinicalCases({ page: 1, limit: 200 }).then((res) => setClinicalCases(res.data?.data || [])).catch(() => {});
    getReferenceValues({ page: 1, limit: 500 }).then((res) => setReferenceValues(res.data?.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const fetchQuestions = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const params = { page, limit, sort_by: "created_at", sort_dir: "desc" };
      if (search) params.search = search;
      if (subjectFilter) params.subject_id = subjectFilter;
      if (statusFilter) params.status = statusFilter;

      const res = await getQuestions(params);
      const body = res.data;
      setQuestions(body.data || []);
      setTotal(body.pagination?.total ?? 0);
      setTotalPages(body.pagination?.totalPages ?? 1);
    } catch (err) {
      setLoadError(err?.response?.data?.message || t("pages.questions.impossibleChargerBanqueQuestions"));
      setQuestions([]);
    } finally {
      setLoading(false);
    }
  }, [page, limit, search, subjectFilter, statusFilter, t]);

  useEffect(() => {
    fetchQuestions();
  }, [fetchQuestions]);

  useEffect(() => {
    if (!feedback) return undefined;
    const timer = setTimeout(() => setFeedback(null), 4000);
    return () => clearTimeout(timer);
  }, [feedback]);

  const label = (list, id) => list.find((x) => x.id === id)?.name || "-";
  const clinicalCaseLabel = (id) => clinicalCases.find((c) => c.id === id)?.title || "-";
  const referenceValueLabel = (id) => {
    const rv = referenceValues.find((r) => r.id === id);
    return rv ? `${rv.name}${rv.unit ? ` (${rv.unit})` : ""}` : "-";
  };

  const resetFilters = () => {
    setSearchInput("");
    setSearch("");
    setSubjectFilter("");
    setStatusFilter("");
    setPage(1);
  };

  const resetImageState = (existing = "") => {
    setImageFile(null);
    setImagePreview((prev) => {
      if (prev && prev.startsWith("blob:")) URL.revokeObjectURL(prev);
      return "";
    });
    setExistingImage(existing);
    setRemoveImage(false);
    setImageError("");
  };

  const handleImageSelect = (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = ""; // permet de re-sélectionner le même fichier
    if (!file) return;
    const problem = validateImageFile(file);
    if (problem) {
      setImageError(t(problem === "type" ? "pages.questions.imageTypeInvalid" : "pages.questions.imageTooLarge"));
      return;
    }
    setImageError("");
    setImageFile(file);
    setRemoveImage(false);
    setImagePreview((prev) => {
      if (prev && prev.startsWith("blob:")) URL.revokeObjectURL(prev);
      return URL.createObjectURL(file);
    });
  };

  const handleImageRemove = () => {
    setImageFile(null);
    setImagePreview((prev) => {
      if (prev && prev.startsWith("blob:")) URL.revokeObjectURL(prev);
      return "";
    });
    setRemoveImage(!!existingImage);
    setImageError("");
  };

  // Libère l'aperçu (URL blob) au démontage de l'écran
  useEffect(() => () => {
    if (imagePreview && imagePreview.startsWith("blob:")) URL.revokeObjectURL(imagePreview);
  }, [imagePreview]);

  const openAddModal = () => {
    resetImageState();
    setFormMode("add");
    setFormData(emptyForm);
    setFormErrors({});
    setSubmitError("");
    setEditingId(null);
    setShowFormModal(true);
  };

  const openEditModal = async (question) => {
    resetImageState(question.image_url || "");
    setFormMode("edit");
    setFormErrors({});
    setSubmitError("");
    setEditingId(question.id);
    setShowFormModal(true);

    // La liste ne contient pas les réponses : on va chercher la question complète.
    try {
      const res = await getQuestionById(question.id);
      const full = res.data?.data || question;
      setExistingImage(full.image_url || "");
      setFormData({
        subject_id: full.subject_id || "",
        category_id: full.category_id || "",
        difficulty_id: full.difficulty_id || "",
        type_id: full.type_id || "",
        content: full.content || "",
        explanation: full.explanation || "",
        pedagogical_reference: full.pedagogical_reference || "",
        points: full.points ?? 1,
        negative_points: full.negative_points ?? 0,
        estimated_duration_seconds: full.estimated_duration_seconds ?? "",
        clinical_case_id: full.clinical_case_id || "",
        case_order_index: full.case_order_index ?? "",
        requires_calculator: !!full.requires_calculator,
        show_reference_values: !!full.show_reference_values,
        reference_value_ids: full.reference_value_ids || [],
        is_active: !!full.is_active,
        answers:
          full.answers && full.answers.length > 0
            ? full.answers.map((a) => ({ content: a.content, is_correct: !!a.is_correct, order_index: a.order_index || 0 }))
            : [emptyAnswer(), emptyAnswer()],
      });
    } catch (err) {
      setSubmitError(t("pages.questions.loadAnswersError"));
    }
  };

  const closeFormModal = () => {
    if (submitting) return;
    setShowFormModal(false);
  };

  const handleFormChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setFormErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const handleAnswerChange = (index, field, value) => {
    setFormData((prev) => {
      const answers = [...prev.answers];
      answers[index] = { ...answers[index], [field]: value };
      return { ...prev, answers };
    });
  };

  const toggleReferenceValue = (id) => {
    setFormData((prev) => {
      const has = prev.reference_value_ids.includes(id);
      return {
        ...prev,
        reference_value_ids: has
          ? prev.reference_value_ids.filter((x) => x !== id)
          : [...prev.reference_value_ids, id],
      };
    });
  };

  const addAnswerRow = () => {
    setFormData((prev) => ({ ...prev, answers: [...prev.answers, emptyAnswer()] }));
  };

  const removeAnswerRow = (index) => {
    setFormData((prev) => ({ ...prev, answers: prev.answers.filter((_, i) => i !== index) }));
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.subject_id) errors.subject_id = t("common.ui.laMatiereEstRequise");
    if (!formData.difficulty_id) errors.difficulty_id = t("pages.questions.difficultyRequired");
    if (!formData.type_id) errors.type_id = t("pages.questions.typeRequired");
    if (!formData.content.trim()) errors.content = t("pages.questions.contentRequired");
    if (formData.answers.length < 2) errors.answers = t("pages.questions.answersMin");
    else if (formData.answers.some((a) => !a.content.trim())) errors.answers = t("pages.questions.answersContent");
    else if (!formData.answers.some((a) => a.is_correct)) errors.answers = t("pages.questions.answersCorrect");
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setSubmitting(true);
    setSubmitError("");
    try {
      const payload = {
        subject_id: formData.subject_id,
        category_id: formData.category_id || null,
        difficulty_id: formData.difficulty_id,
        type_id: formData.type_id,
        content: formData.content.trim(),
        explanation: formData.explanation.trim() || null,
        pedagogical_reference: formData.pedagogical_reference.trim() || null,
        points: Number(formData.points) || 1,
        negative_points: Number(formData.negative_points) || 0,
        estimated_duration_seconds:
          formData.estimated_duration_seconds === "" ? null : Number(formData.estimated_duration_seconds),
        clinical_case_id: formData.clinical_case_id || null,
        case_order_index:
          formData.clinical_case_id && formData.case_order_index !== ""
            ? Number(formData.case_order_index)
            : null,
        requires_calculator: formData.requires_calculator,
        show_reference_values: formData.show_reference_values,
        reference_value_ids: formData.reference_value_ids,
        is_active: formData.is_active,
        answers: formData.answers.map((a, i) => ({
          content: a.content.trim(),
          is_correct: !!a.is_correct,
          order_index: i,
        })),
      };

      let questionId = editingId;
      if (formMode === "add") {
        const created = await createQuestion(payload);
        questionId = created?.data?.data?.id;
        setFeedback({ type: "success", message: t("pages.questions.createSuccess") });
      } else {
        await updateQuestion(editingId, payload);
        setFeedback({ type: "success", message: t("pages.questions.updateSuccess") });
      }
      // L'image est envoyée une fois la question enregistrée (multipart séparé).
      if (questionId && (imageFile || removeImage)) {
        try {
          if (imageFile) await uploadQuestionImage(questionId, imageFile);
          else await deleteQuestionImage(questionId);
        } catch (imgErr) {
          setFeedback({
            type: "warning",
            message: imgErr?.response?.data?.message || t(imageFile ? "pages.questions.imageUploadError" : "pages.questions.imageRemoveError"),
          });
        }
      }
      setShowFormModal(false);
      fetchQuestions();
    } catch (err) {
      setSubmitError(err?.response?.data?.message || t("common.crud.saveError"));
    } finally {
      setSubmitting(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteQuestion(deleteTarget.id);
      setFeedback({ type: "success", message: t("pages.questions.deleteSuccess") });
      setDeleteTarget(null);
      if (questions.length === 1 && page > 1) setPage((p) => p - 1);
      else fetchQuestions();
    } catch (err) {
      setFeedback({ type: "danger", message: err?.response?.data?.message || t("pages.questions.deleteError") });
      setDeleteTarget(null);
    } finally {
      setDeleting(false);
    }
  };

  const toggleActive = async (question) => {
    try {
      await updateQuestion(question.id, { is_active: !question.is_active });
      setQuestions((prev) => prev.map((q) => (q.id === question.id ? { ...q, is_active: !question.is_active } : q)));
    } catch (err) {
      setFeedback({ type: "danger", message: t("common.crud.toggleError") });
    }
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to="/dashboard">{t("menu.dashboard.title")}</Link>
                </li>
                <li className="breadcrumb-item active">{t("menu.questionBank.title")}</li>
              </ol>
            </nav>
          </div>
          <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
            <div className="pe-1 mb-2">
              <button type="button" className="btn btn-outline-light bg-white btn-icon me-1" onClick={resetFilters}>
                <i className="ti ti-refresh"></i>
              </button>
            </div>
            <div className="mb-2">
              {canCreate && (
                <button type="button" className="btn btn-primary d-flex align-items-center" onClick={openAddModal}>
                  <i className="ti ti-square-rounded-plus me-2"></i>{t("pages.questions.addLabel")}
                </button>
              )}
            </div>
          </div>
        </div>

        {feedback && (
          <div className={`alert alert-${feedback.type} alert-dismissible fade show`} role="alert">
            {feedback.message}
            <button type="button" className="btn-close" onClick={() => setFeedback(null)}></button>
          </div>
        )}

        <div className="card">
          <div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-0">
            <h4 className="mb-3">{t("pages.questions.title")}</h4>
            <div className="d-flex align-items-center flex-wrap">
              <div className="input-icon-start mb-3 me-2 position-relative">
                <span className="icon-addon"></span>
                <input
                  type="text"
                  className="form-control"
                  placeholder={t("pages.questions.searchPlaceholder")}
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  style={{ paddingLeft: "35px", minWidth: "220px" }}
                />
              </div>
              <div className="mb-3 me-2">
                <select
                  className="form-select"
                  value={subjectFilter}
                  onChange={(e) => {
                    setSubjectFilter(e.target.value);
                    setPage(1);
                  }}
                >
                  <option value="">{t("common.fields.allSubjects")}</option>
                  {subjects.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </div>
              {/* <div className="mb-3 me-2">
                <select
                  className="form-select"
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setPage(1);
                  }}
                >
                  <option value="">{t("common.crud.allStatuses")}</option>
                  <option value="draft">{t("common.examStatus.draft")}</option>
                  <option value="validated">{t("common.ui.validee")}</option>
                  <option value="archived">{t("common.ui.archivee")}</option>
                </select>
              </div> */}
            </div>
          </div>

          <DataTablePagination
            variant="header"
            page={page}
            limit={limit}
            total={total}
            totalPages={totalPages}
            onPageChange={setPage}
            onLimitChange={(v) => {
              setLimit(v);
              setPage(1);
            }}
          />

          <div className="card-body p-0 py-3">
            <div className="custom-datatable-filter table-responsive">
              <table className="table datatable">
                <thead className="thead-light">
                  <tr>
                    <th>{t("pages.questions.statementColumn")}</th>
                    <th>{t("common.fields.subject")}</th>
                    <th>{t("common.fields.difficulty")}</th>
                    <th>{t("common.fields.type")}</th>
                    <th>{t("pages.questions.casClinique")}</th>
                    <th>{t("common.fields.points")}</th>
                    <th>{t("pages.questions.utilisations")}</th>
                    {/* <th>{t("pages.questions.validation")}</th> */}
                    <th>{t("common.crud.statusActive")}</th>
                    <th>{t("common.crud.actionColumn")}</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="10" className="text-center py-4">
                        <div className="spinner-border text-primary" role="status">
                          <span className="visually-hidden">{t("common.crud.loading")}</span>
                        </div>
                      </td>
                    </tr>
                  ) : loadError ? (
                    <tr>
                      <td colSpan="10" className="text-center py-4 text-danger">
                        {loadError}
                      </td>
                    </tr>
                  ) : questions.length > 0 ? (
                    questions.map((question) => (
                      <tr key={question.id}>
                        <td>
                          {question.image_url && (
                            <img
                              src={resolveUploadUrl(question.image_url)}
                              alt={t("pages.questions.imageAlt")}
                              style={{ width: "36px", height: "36px", objectFit: "cover" }}
                              className="rounded border me-2 align-middle"
                            />
                          )}
                          <span className="text-truncate d-inline-block align-middle" style={{ maxWidth: "280px" }} title={question.content}>
                            {question.content}
                          </span>
                        </td>
                        <td>{label(subjects, question.subject_id)}</td>
                        <td>{label(difficulties, question.difficulty_id)}</td>
                        <td>{label(types, question.type_id)}</td>
                        <td>
                          {question.clinical_case_id ? (
                            <span className="badge badge-soft-info">
                              {clinicalCaseLabel(question.clinical_case_id)}
                              {question.case_order_index !== null && question.case_order_index !== undefined
                                ? ` (#${question.case_order_index})`
                                : ""}
                            </span>
                          ) : (
                            "-"
                          )}
                        </td>
                        <td>{question.points}</td>
                        <td>{question.usage_count ?? 0}</td>
                        {/* <td>
                          <span className={`badge badge-soft-${statusBadge[question.status] || "secondary"}`}>
                            {statusLabels[question.status] || question.status || t("common.examStatus.draft")}
                          </span>
                        </td> */}
                        <td>
                          <span
                            className={`badge badge-soft-${question.is_active ? "success" : "danger"} d-inline-flex align-items-center`}
                            role={canUpdate ? "button" : undefined}
                            onClick={canUpdate ? () => toggleActive(question) : undefined}
                          >
                            <i className="ti ti-circle-filled fs-5 me-1"></i>
                            {question.is_active ? t("common.ui.active") : t("common.ui.inactive")}
                          </span>
                        </td>
                        <td>
                          <Dropdown>
                            <Dropdown.Toggle
                              as="button"
                              className="btn btn-white btn-icon btn-sm d-flex align-items-center justify-content-center rounded-circle p-0 i-false"
                            >
                              <i className="ti ti-dots-vertical fs-14"></i>
                            </Dropdown.Toggle>
                            <Dropdown.Menu align="end" className="p-3">
                              {canUpdate && (
                                <Dropdown.Item className="rounded-1" onClick={() => openEditModal(question)}>
                                  <i className="ti ti-edit-circle me-2"></i>{t("common.crud.edit")}
                                </Dropdown.Item>
                              )}
                              {canUpdate && (
                                <Dropdown.Item className="rounded-1" onClick={() => toggleActive(question)}>
                                  <i className="ti ti-toggle-right me-2"></i>
                                  {question.is_active ? t("common.crud.deactivate") : t("common.crud.activate")}
                                </Dropdown.Item>
                              )}
                              {canDelete && (
                                <Dropdown.Item className="rounded-1" onClick={() => setDeleteTarget(question)}>
                                  <i className="ti ti-trash-x me-2"></i>{t("common.crud.delete")}
                                </Dropdown.Item>
                              )}
                            </Dropdown.Menu>
                          </Dropdown>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="10" className="text-center py-4">
                        <i className="ti ti-search-off fs-1 text-muted"></i>
                        <h5 className="mt-2">{t("pages.questions.noQuestionsFound")}</h5>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <DataTablePagination
              variant="footer"
              page={page}
              limit={limit}
              total={total}
              totalPages={totalPages}
              onPageChange={setPage}
              onLimitChange={(v) => {
                setLimit(v);
                setPage(1);
              }}
            />
          </div>
        </div>
      </div>

      <Modal show={showFormModal} onHide={closeFormModal} centered size="lg">
        <form onSubmit={handleSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>{formMode === "add" ? t("pages.questions.addLabel") : t("pages.questions.editTitle")}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {submitError && <div className="alert alert-danger">{submitError}</div>}
            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">
                  {t("common.fields.subject")} <span className="text-danger">*</span>
                </label>
                <select
                  className={`form-select ${formErrors.subject_id ? "is-invalid" : ""}`}
                  value={formData.subject_id}
                  onChange={(e) => handleFormChange("subject_id", e.target.value)}
                >
                  <option value="">{t("common.crud.selectPlaceholder")}</option>
                  {subjects.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
                {formErrors.subject_id && <div className="invalid-feedback d-block">{formErrors.subject_id}</div>}
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">{t("pages.questions.categoryOptional")}</label>
                <select
                  className="form-select"
                  value={formData.category_id}
                  onChange={(e) => handleFormChange("category_id", e.target.value)}
                >
                  <option value="">{t("common.fields.noneFem")}</option>
                  {categories
                    .filter((c) => !formData.subject_id || c.subject_id === formData.subject_id)
                    .map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                </select>
              </div>
            </div>
            <div className="row">
              <div className="col-md-4 mb-3">
                <label className="form-label">
                  {t("common.fields.difficulty")} <span className="text-danger">*</span>
                </label>
                <select
                  className={`form-select ${formErrors.difficulty_id ? "is-invalid" : ""}`}
                  value={formData.difficulty_id}
                  onChange={(e) => handleFormChange("difficulty_id", e.target.value)}
                >
                  <option value="">{t("common.crud.selectPlaceholder")}</option>
                  {difficulties.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.name}
                    </option>
                  ))}
                </select>
                {formErrors.difficulty_id && <div className="invalid-feedback d-block">{formErrors.difficulty_id}</div>}
              </div>
              <div className="col-md-4 mb-3">
                <label className="form-label">
                  {t("common.fields.type")} <span className="text-danger">*</span>
                </label>
                <select
                  className={`form-select ${formErrors.type_id ? "is-invalid" : ""}`}
                  value={formData.type_id}
                  onChange={(e) => handleFormChange("type_id", e.target.value)}
                >
                  <option value="">{t("common.crud.selectPlaceholder")}</option>
                  {types.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
                </select>
                {formErrors.type_id && <div className="invalid-feedback d-block">{formErrors.type_id}</div>}
              </div>
              <div className="col-md-2 mb-3">
                <label className="form-label">{t("common.fields.points")}</label>
                <input
                  type="number"
                  className="form-control"
                  value={formData.points}
                  onChange={(e) => handleFormChange("points", e.target.value)}
                />
              </div>
              <div className="col-md-2 mb-3">
                <label className="form-label">{t("pages.questions.negativePoints")}</label>
                <input
                  type="number"
                  className="form-control"
                  value={formData.negative_points}
                  onChange={(e) => handleFormChange("negative_points", e.target.value)}
                />
              </div>
            </div>
            <div className="mb-3">
              <label className="form-label">
                {t("pages.questions.statementColumn")} <span className="text-danger">*</span>
              </label>
              <textarea
                className={`form-control ${formErrors.content ? "is-invalid" : ""}`}
                rows={3}
                value={formData.content}
                onChange={(e) => handleFormChange("content", e.target.value)}
              ></textarea>
              {formErrors.content && <div className="invalid-feedback d-block">{formErrors.content}</div>}
            </div>
            <div className="mb-3">
              <label className="form-label">{t("pages.questions.imageLabel")}</label>
              {(imagePreview || (existingImage && !removeImage)) && (
                <div className="mb-2 d-flex align-items-start gap-3">
                  <img
                    src={imagePreview || resolveUploadUrl(existingImage)}
                    alt={t("pages.questions.imageAlt")}
                    style={{ maxWidth: "260px", maxHeight: "180px", objectFit: "contain" }}
                    className="border rounded p-1 bg-white"
                  />
                  <button type="button" className="btn btn-outline-danger btn-sm" onClick={handleImageRemove} disabled={submitting}>
                    {t("pages.questions.imageRemove")}
                  </button>
                </div>
              )}
              <input
                type="file"
                className={`form-control ${imageError ? "is-invalid" : ""}`}
                accept={IMAGE_ACCEPT}
                onChange={handleImageSelect}
                disabled={submitting}
              />
              {imageError ? (
                <div className="invalid-feedback d-block">{imageError}</div>
              ) : (
                <small className="text-muted">{t("pages.questions.imageHint")}</small>
              )}
            </div>
            <div className="mb-3">
              <label className="form-label">{t("pages.questions.explanationOptional")}</label>
              <textarea
                className="form-control"
                rows={2}
                value={formData.explanation}
                onChange={(e) => handleFormChange("explanation", e.target.value)}
              ></textarea>
            </div>
            <div className="row">
              <div className="col-md-8 mb-3">
                <label className="form-label">{t("pages.questions.referencePedagogiqueOptionnel")}</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder={t("pages.questions.exCoursPharmacologieChapitre4")}
                  value={formData.pedagogical_reference}
                  onChange={(e) => handleFormChange("pedagogical_reference", e.target.value)}
                />
              </div>
              <div className="col-md-4 mb-3">
                <label className="form-label">{t("pages.questions.dureeEstimeeSecondes")}</label>
                <input
                  type="number"
                  min="0"
                  className="form-control"
                  value={formData.estimated_duration_seconds}
                  onChange={(e) => handleFormChange("estimated_duration_seconds", e.target.value)}
                />
              </div>
            </div>

            <hr />
            <h6 className="fw-bold mb-2">{t("pages.questions.casClinique")}</h6>
            <div className="form-text mb-2">
              {t("pages.questions.rattachezQuestionCasCliniquePour")}
            </div>
            <div className="row">
              <div className="col-md-8 mb-3">
                <label className="form-label">{t("pages.questions.casCliniqueOptionnel")}</label>
                <select
                  className="form-select"
                  value={formData.clinical_case_id}
                  onChange={(e) => {
                    handleFormChange("clinical_case_id", e.target.value);
                    if (!e.target.value) handleFormChange("case_order_index", "");
                  }}
                >
                  <option value="">{t("pages.questions.aucunQuestionIndependante")}</option>
                  {clinicalCases.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.title}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-4 mb-3">
                <label className="form-label">{t("pages.questions.ordreDansCas")}</label>
                <input
                  type="number"
                  min="0"
                  className="form-control"
                  disabled={!formData.clinical_case_id}
                  value={formData.case_order_index}
                  onChange={(e) => handleFormChange("case_order_index", e.target.value)}
                />
              </div>
            </div>

            <hr />
            <h6 className="fw-bold mb-2">{t("pages.questions.calculatriceValeursReference")}</h6>
            <div className="form-text mb-2">
              {t("pages.questions.pourQuestionsDosageCalculatrice")}
            </div>
            <div className="row">
              <div className="col-md-6 mb-3">
                <div className="form-check form-switch">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    role="switch"
                    id="question-requires-calculator"
                    checked={formData.requires_calculator}
                    onChange={(e) => handleFormChange("requires_calculator", e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="question-requires-calculator">
                    {t("pages.questions.necessiteCalculatrice")}
                  </label>
                </div>
              </div>
              <div className="col-md-6 mb-3">
                <div className="form-check form-switch">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    role="switch"
                    id="question-show-reference-values"
                    checked={formData.show_reference_values}
                    onChange={(e) => handleFormChange("show_reference_values", e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="question-show-reference-values">
                    {t("pages.questions.afficherValeursReferenceBiologique")}
                  </label>
                </div>
              </div>
            </div>
            {formData.show_reference_values && (
              <div className="mb-3">
                <label className="form-label">{t("pages.questions.valeursReferenceAfficher")}</label>
                <div className="border rounded p-2" style={{ maxHeight: "160px", overflowY: "auto" }}>
                  {referenceValues.length === 0 ? (
                    <div className="text-muted small">{t("pages.questions.aucuneValeurReferenceDisponible")}</div>
                  ) : (
                    referenceValues.map((rv) => (
                      <div className="form-check" key={rv.id}>
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id={`rv-${rv.id}`}
                          checked={formData.reference_value_ids.includes(rv.id)}
                          onChange={() => toggleReferenceValue(rv.id)}
                        />
                        <label className="form-check-label" htmlFor={`rv-${rv.id}`}>
                          {rv.name}
                          {rv.unit ? ` (${rv.unit})` : ""}
                          {rv.category ? ` — ${rv.category}` : ""}
                        </label>
                      </div>
                    ))
                  )}
                </div>
                {formData.reference_value_ids.length > 0 && (
                  <div className="form-text">
                    {t("pages.questions.selectionnees")} {formData.reference_value_ids.map((id) => referenceValueLabel(id)).join(", ")}
                  </div>
                )}
              </div>
            )}

            <hr />
            <div className="d-flex align-items-center justify-content-between mb-2">
              <label className="form-label fw-bold mb-0">
                {t("pages.questions.answersLabel")} <span className="text-danger">*</span>
              </label>
              <button type="button" className="btn btn-sm btn-outline-primary" onClick={addAnswerRow}>
                <i className="ti ti-plus me-1"></i>{t("pages.questions.addAnswer")}
              </button>
            </div>
            {formErrors.answers && <div className="text-danger small mb-2">{formErrors.answers}</div>}
            {formData.answers.map((answer, index) => (
              <div className="d-flex align-items-center gap-2 mb-2" key={index}>
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    title={t("pages.questions.correctAnswerHint")}
                    checked={answer.is_correct}
                    onChange={(e) => handleAnswerChange(index, "is_correct", e.target.checked)}
                  />
                </div>
                <input
                  type="text"
                  className="form-control"
                  placeholder={t("pages.questions.answerPlaceholder", { n: index + 1 })}
                  value={answer.content}
                  onChange={(e) => handleAnswerChange(index, "content", e.target.value)}
                />
                {formData.answers.length > 2 && (
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger"
                    onClick={() => removeAnswerRow(index)}
                  >
                    <i className="ti ti-x"></i>
                  </button>
                )}
              </div>
            ))}
            <div className="form-text">{t("pages.questions.checkCorrectHint")}</div>

            <div className="form-check form-switch mt-3">
              <input
                className="form-check-input"
                type="checkbox"
                role="switch"
                id="question-is-active"
                checked={formData.is_active}
                onChange={(e) => handleFormChange("is_active", e.target.checked)}
              />
              <label className="form-check-label" htmlFor="question-is-active">
                {t("pages.questions.activeLabel")}
              </label>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <button type="button" className="btn btn-light" onClick={closeFormModal} disabled={submitting}>
              {t("common.crud.cancel")}
            </button>
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? t("common.crud.saving") : formMode === "add" ? t("common.crud.add") : t("common.crud.save")}
            </button>
          </Modal.Footer>
        </form>
      </Modal>

      <Modal show={deleteTarget !== null} onHide={() => (deleting ? null : setDeleteTarget(null))} centered>
        <div className="modal-body text-center">
          <span className="delete-icon">
            <i className="ti ti-trash-x"></i>
          </span>
          <h4>{t("common.crud.confirmDeleteTitle")}</h4>
          <p>{t("pages.questions.confirmDeleteBody")}</p>
          <div className="d-flex justify-content-center">
            <button type="button" className="btn btn-light me-3" onClick={() => setDeleteTarget(null)} disabled={deleting}>
              {t("common.crud.cancel")}
            </button>
            <button type="button" className="btn btn-danger" onClick={confirmDelete} disabled={deleting}>
              {deleting ? t("common.crud.deleting") : t("common.crud.confirmDelete")}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Questions;
